<template>
  <div>
    <!-- ═══ SIDEBAR (esquerda) ═══ -->
    <aside class="sidebar" :class="{ 'sidebar--collapsed': isCollapsed }">
      <b-loading
        :is-full-page="true"
        :active="isLoading"
        :can-cancel="false"
      ></b-loading>

      <!-- Logo -->
      <div class="sidebar__header">
        <router-link to="/dashboard" class="sidebar__logo-link">
          <img
            src="../../assets/img/logo-smart-orange-outline.png"
            alt="Onii"
            class="sidebar__logo"
          />
        </router-link>
      </div>

      <!-- Nome da página + botão colapsar -->
      <div class="sidebar__section-title">
        <span class="sidebar__section-label">Partner</span>
        <button
          class="sidebar__toggle"
          @click="toggleSidebar"
          :title="isCollapsed ? 'Expandir menu' : 'Recolher menu'"
        >
          <b-icon
            :icon="isCollapsed ? 'chevron-right' : 'chevron-left'"
            type="is-white"
            size="is-small"
          />
        </button>
      </div>

      <!-- Navegação -->
      <nav class="sidebar__nav">
        <router-link
          to="/dashboard"
          class="sidebar__item"
          :title="isCollapsed ? $t('dashboard.stores') : ''"
        >
          <b-icon icon="store" type="is-white" size="is-small" />
          <span class="sidebar__label">{{ $t("dashboard.stores") }}</span>
        </router-link>

        <a
          class="sidebar__item"
          @click="goToTransactions"
          :title="isCollapsed ? 'Transações' : ''"
        >
          <b-icon icon="exchange-alt" type="is-white" size="is-small" />
          <span class="sidebar__label">Transações</span>
        </a>

        <a
          class="sidebar__item"
          @click="goToProducts"
          :title="isCollapsed ? 'Produtos' : ''"
        >
          <b-icon icon="box" type="is-white" size="is-small" />
          <span class="sidebar__label">Produtos</span>
        </a>

        <a
          class="sidebar__item"
          @click="goToAccesses"
          :title="isCollapsed ? 'Acessos' : ''"
        >
          <b-icon icon="door-open" type="is-white" size="is-small" />
          <span class="sidebar__label">Acessos</span>
        </a>

        <a
          class="sidebar__item"
          v-if="permissions.canSeeUserStoreFeedbacks"
          @click="goToFeedbacks"
          :title="isCollapsed ? 'Feedbacks' : ''"
        >
          <b-icon icon="comments" type="is-white" size="is-small" />
          <span class="sidebar__label">Feedbacks</span>
        </a>

        <router-link
          to="/vendings"
          class="sidebar__item"
          v-if="permissions.vending && permissions.vending.canSeeDashboard"
          :title="isCollapsed ? $t('dashboard.vendings') : ''"
        >
          <b-icon icon="shopping-cart" type="is-white" size="is-small" />
          <span class="sidebar__label">{{ $t("dashboard.vendings") }}</span>
        </router-link>

        <router-link
          to="/buy-now-pay-later"
          class="sidebar__item"
          v-if="permissions.manageBuyNowPayLater"
          :title="isCollapsed ? $t('dashboard.buyNowPayLater') : ''"
        >
          <b-icon icon="credit-card" type="is-white" size="is-small" />
          <span class="sidebar__label">{{
            $t("dashboard.buyNowPayLater")
          }}</span>
        </router-link>
      </nav>
    </aside>

    <!-- ═══ TOPBAR (direita, acima do conteúdo) ═══ -->
    <div class="topbar">
      <div class="topbar__user" @click="toggleUserMenu">
        <div class="topbar__avatar">{{ userInitials }}</div>
        <span class="topbar__name">{{ fullName }}</span>
        <b-icon
          :icon="userMenuOpen ? 'chevron-up' : 'chevron-down'"
          type="is-white"
          size="is-small"
          class="topbar__chevron"
        />
      </div>

      <!-- Dropdown -->
      <transition name="dropdown-fade">
        <div v-if="userMenuOpen" class="topbar__dropdown">
          <router-link
            to="/account"
            class="topbar__dropdown-item"
            @click.native="userMenuOpen = false"
          >
            <b-icon icon="user-cog" size="is-small" />
            <span>Cadastro</span>
          </router-link>
          <router-link
            to="/contact"
            class="topbar__dropdown-item"
            @click.native="userMenuOpen = false"
          >
            <b-icon icon="envelope" size="is-small" />
            <span>Contato</span>
          </router-link>
          <hr class="topbar__divider" />
          <a
            class="topbar__dropdown-item topbar__dropdown-logout"
            @click="handleLogout"
          >
            <b-icon icon="sign-out-alt" size="is-small" />
            <span>Sair</span>
          </a>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { common } from "../../mixins/common";

let activeInstances = 0;

export default {
  mixins: [common],

  data() {
    return {
      isLoading: false,
      isCollapsed: localStorage.getItem("sidebarCollapsed") === "true",
      userMenuOpen: false
    };
  },

  computed: {
    ...mapGetters(["userInfo", "permissions"]),

    fullName() {
      return `${this.userInfo.name} ${this.userInfo.surname}`;
    },

    userInitials() {
      const n = (this.userInfo.name || "").charAt(0).toUpperCase();
      const s = (this.userInfo.surname || "").charAt(0).toUpperCase();
      return n + s;
    }
  },

  methods: {
    toggleSidebar() {
      this.isCollapsed = !this.isCollapsed;
      localStorage.setItem("sidebarCollapsed", String(this.isCollapsed));
      this.updateSidebarWidth();
    },

    updateSidebarWidth() {
      document.documentElement.style.setProperty(
        "--sidebar-width",
        this.isCollapsed ? "64px" : "220px"
      );
    },

    toggleUserMenu() {
      this.userMenuOpen = !this.userMenuOpen;
    },

    handleLogout() {
      this.userMenuOpen = false;
      this.logout();
    },

    goToTransactions() {
      this.$store
        .dispatch("setStoreBeingManaged", null)
        .then(() => this.$router.push("/transactions"));
    },
    goToProducts() {
      this.$store
        .dispatch("setStoreBeingManaged", null)
        .then(() => this.$router.push("/products"));
    },
    goToAccesses() {
      this.$store
        .dispatch("setStoreBeingManaged", null)
        .then(() => this.$router.push("/accesses"));
    },
    goToFeedbacks() {
      this.$store
        .dispatch("setStoreBeingManaged", null)
        .then(() => this.$router.push("/feedbacks"));
    },

    onClickOutside(e) {
      if (this.userMenuOpen && !this.$el.contains(e.target)) {
        this.userMenuOpen = false;
      }
    }
  },

  created() {
    activeInstances++;
    this.updateSidebarWidth();
    document.documentElement.style.setProperty("--topbar-height", "52px");
  },

  mounted() {
    document.addEventListener("click", this.onClickOutside);

    this.isLoading = true;
    this.$store
      .dispatch("getAccountData", this.userInfo.email)
      .then(res => {
        if (res.data.result === "error") {
          this.alertError(res.data);
        }
      })
      .catch(() => {
        this.alertError({ message: "UNEXPECTED_ERROR" });
      })
      .finally(() => {
        this.isLoading = false;
      });
  },

  beforeDestroy() {
    document.removeEventListener("click", this.onClickOutside);
    activeInstances--;
    if (activeInstances === 0) {
      document.documentElement.style.removeProperty("--sidebar-width");
      document.documentElement.style.removeProperty("--topbar-height");
    }
  }
};
</script>

<style scoped>
/* ══════════════════════════════════
   SIDEBAR
══════════════════════════════════ */
.sidebar {
  position: fixed;
  top: 0;
  left: 0;
  width: 220px;
  height: 100vh;
  background-color: #0a0a0a;
  display: flex;
  flex-direction: column;
  z-index: 40;
  transition: width 0.2s ease;
  overflow: hidden;
}
.sidebar--collapsed {
  width: 64px;
}

/* Header da sidebar */
.sidebar__header {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 52px;
  padding: 0 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  flex-shrink: 0;
}
.sidebar--collapsed .sidebar__header {
  padding: 0;
}
.sidebar__logo-link {
  display: flex;
  align-items: center;
  justify-content: center;
}
.sidebar__logo {
  width: 46px;
  height: auto;
  display: block;
}
.sidebar--collapsed .sidebar__logo {
  width: 46px;
}

/* Nome da página + toggle */
.sidebar__section-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem 0.6rem 0.5rem 1rem;
  flex-shrink: 0;
  min-height: 38px;
}
.sidebar--collapsed .sidebar__section-title {
  justify-content: center;
  padding: 0.5rem 0;
}
.sidebar__section-label {
  font-size: 0.75rem;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.9);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  white-space: nowrap;
  overflow: hidden;
  opacity: 1;
  transition: opacity 0.1s ease, max-width 0.2s ease;
  max-width: 160px;
}
.sidebar--collapsed .sidebar__section-label {
  opacity: 0;
  max-width: 0;
}

/* Toggle */
.sidebar__toggle {
  background: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  border-radius: 4px;
  opacity: 1;
  transition: background 0.15s;
  flex-shrink: 0;
}
.sidebar__toggle:hover {
  background: rgba(255, 255, 255, 0.12);
}

/* Nav */
.sidebar__nav {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 0.5rem 0;
}

/* Itens */
.sidebar__item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.65rem 1rem;
  color: rgba(255, 255, 255, 0.65);
  text-decoration: none;
  cursor: pointer;
  overflow: hidden;
  border-left: 3px solid transparent;
  transition: background 0.15s, color 0.15s, border-color 0.15s;
  white-space: nowrap;
}
.sidebar--collapsed .sidebar__item {
  justify-content: center;
  gap: 0;
  padding: 0.65rem 0;
}
.sidebar__item:hover {
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.9);
}
.sidebar__item.router-link-active {
  background: rgba(240, 81, 45, 0.12);
  color: #fff;
  border-left-color: #f0512d;
}
.sidebar--collapsed .sidebar__item.router-link-active {
  border-left-color: transparent;
  background: rgba(240, 81, 45, 0.15);
}

/* Labels */
.sidebar__label {
  font-size: 0.875rem;
  white-space: nowrap;
  overflow: hidden;
  max-width: 160px;
  opacity: 1;
  transition: opacity 0.1s ease, max-width 0.2s ease;
}
.sidebar--collapsed .sidebar__label {
  opacity: 0;
  max-width: 0;
}

/* ══════════════════════════════════
   TOPBAR (direita, acima do conteúdo)
══════════════════════════════════ */
.topbar {
  position: fixed;
  top: 0;
  left: var(--sidebar-width, 220px);
  right: 0;
  height: 52px;
  background: #0a0a0a;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 0 1.5rem;
  z-index: 30;
  transition: left 0.2s ease;
}

/* Widget do usuário */
.topbar__user {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  padding: 0.35rem 0.6rem;
  border-radius: 6px;
  transition: background 0.15s;
  user-select: none;
}
.topbar__user:hover {
  background: rgba(255, 255, 255, 0.08);
}

.topbar__avatar {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: rgba(240, 81, 45, 0.25);
  color: #f0512d;
  font-size: 0.65rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  letter-spacing: 0.5px;
}

.topbar__name {
  font-size: 0.875rem;
  font-weight: 500;
  color: #fff;
  white-space: nowrap;
}

.topbar__chevron {
  opacity: 0.65;
}

/* Dropdown */
.topbar__dropdown {
  position: absolute;
  top: calc(100% + 4px);
  right: 1rem;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  min-width: 170px;
  padding: 0.25rem 0;
  z-index: 50;
}

.topbar__dropdown-item {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.6rem 1rem;
  color: #444;
  text-decoration: none;
  cursor: pointer;
  font-size: 0.875rem;
  white-space: nowrap;
  transition: background 0.12s;
}
.topbar__dropdown-item:hover {
  background: #f9f9f9;
  color: #111;
}
.topbar__dropdown-logout {
  color: #888;
}

.topbar__divider {
  height: 1px;
  background: #eee;
  border: none;
  margin: 0.25rem 0;
}

/* Animação */
.dropdown-fade-enter-active,
.dropdown-fade-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}
.dropdown-fade-enter,
.dropdown-fade-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}
</style>
